import React, { useState, useEffect, useMemo } from 'react';
import { useCondominium } from '../context/CondominiumContext';
import { useUser } from '../context/UserContext';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { Property } from '../types';
import { supabase } from '../integrations/supabase/client';
import { generatePropertyCode } from '../utils/propertyCodeGenerator';
import { formatCurrencyFromCents, parseCurrency, formatPhone, parsePhone } from '../utils/masks';
import { translateAmenity } from '../utils/translations';
import { useToast } from '../hooks/use-toast';
import { 
  Save, 
  X, 
  MapPin,
  Building,
  DollarSign,
  Ruler,
  CheckSquare,
  Square,
  Upload,
  Image as ImageIcon,
  Trash2,
  Camera,
  User,
  Star,
  FileText,
  Zap,
  GripVertical
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { uploadImageWithWatermark, processImageFile } from '../utils/watermark';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Componente para imagem sortável
interface SortableImageItemProps {
  image: any;
  onSetPrimary: (id: string) => void;
  onRemove: (id: string) => void;
}

const SortableImageItem: React.FC<SortableImageItemProps> = ({ image, onSetPrimary, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="relative group"
    >
      <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
        <img
          src={image.preview || image.url}
          alt={image.name}
          className="w-full h-full object-cover"
        />
      </div>
      
      {/* Badge de imagem principal */}
      {image.primary && (
        <div className="absolute top-2 left-2 bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-medium">
          Principal
        </div>
      )}
      
      {/* Handle de arraste */}
      <div 
        {...attributes} 
        {...listeners}
        className="absolute top-2 right-2 bg-black bg-opacity-50 text-white p-1 rounded cursor-grab hover:bg-opacity-70 transition-opacity z-20"
        title="Arrastar para reordenar"
      >
        <GripVertical className="w-4 h-4" />
      </div>
      
      {/* Overlay com ações */}
      <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center space-x-2">
        <button
          type="button"
          onClick={() => onSetPrimary(image.id!)}
          className="bg-orange-500 text-white p-2 rounded-full hover:bg-orange-600 transition-colors"
          title="Definir como principal"
        >
          <Star className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => onRemove(image.id!)}
          className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
          title="Remover imagem"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      
      {/* Nome do arquivo */}
      <p className="text-xs text-gray-500 mt-1 truncate" title={image.name}>
        {image.name}
      </p>
    </div>
  );
};

interface PropertyFormProps {
  property?: Property; // Se existir, modo EDIÇÃO; se não, modo CRIAÇÃO
  initialPropertyType?: string;
  initialCondominiumId?: string;
  onSave: (property: Property) => void;
  onCancel: () => void;
}

const PropertyForm: React.FC<PropertyFormProps> = ({ 
  property, 
  initialPropertyType,
  initialCondominiumId,
  onSave, 
  onCancel 
}) => {
  const { getCondominiumById, condominiums } = useCondominium();
  const { currentUser } = useUser();
  const { user: authUser } = useAuth();
  const { isManagerOrDirector } = usePermissions();
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState('');
  const [displayOwnerPhone, setDisplayOwnerPhone] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [condominiumImages, setCondominiumImages] = useState<any[]>([]);
  const [corretores, setCorretores] = useState<Array<{id: string, name: string}>>([]);
  
  // Estados para valores formatados
  const [displayValues, setDisplayValues] = useState({
    listPrice: '',
    rentalPrice: '',
    propertyAdministrationFee: '',
    yearlyTax: ''
  });
  
  // Função para lidar com mudança de valores monetários
  const handleCurrencyChange = (field: string, value: string) => {
    const centsValue = parseCurrency(value);
    const reaisValue = centsValue / 100;
    console.log(`💰 handleCurrencyChange: ${field} = "${value}" -> ${centsValue} centavos = ${reaisValue} reais`);
    handleInputChange(field, reaisValue);
    setDisplayValues(prev => ({
      ...prev,
      [field]: formatCurrencyFromCents(centsValue)
    }));
  };
  const [loadingCorretores, setLoadingCorretores] = useState(false);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [isProcessingImages, setIsProcessingImages] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();
  
  const [formData, setFormData] = useState<Partial<Property>>({
    listingId: '',
    propertyCode: '',
    title: '',
    transactionType: 'Venda',
    publicationType: 'STANDARD',
    listDate: new Date().toISOString(),
    lastUpdateDate: new Date().toISOString(),
    detailViewUrl: '',
    propertyType: initialPropertyType || '',
    description: '',
    listPrice: 0,
    rentalPrice: undefined,
    propertyAdministrationFee: undefined,
    yearlyTax: undefined,
    livingArea: 0,
    totalArea: undefined,
    bedrooms: 0,
    suites: 0,
    bathrooms: 0,
    garage: {
      quantity: 0,
      type: 'Covered'
    },
    features: [],
    location: {
      country: 'Brasil',
      state: 'SP',
      city: '',
      neighborhood: '',
      address: '',
      streetNumber: '',
      complement: '',
      postalCode: '',
      latitude: 0,
       longitude: 0
     },
     tower: "",
     media: {
       images: [],
       youtubeVideoUrl: 'https://www.youtube.com/watch?v=pdapqYNNHLE',
       virtualTourUrl: ''
     },
    contact: {
      brokerName: 'FOX VENDAS',
      brokerEmail: 'victor@foxvendas.com.br',
      brokerPhone: '19 32178399',
      brokerPhoto: '',
      companySite: 'https://foxvendas.com.br',
      companyLogo: 'https://foxvendas.com.br/logo.png'
    },
    owner: {
      name: '',
      phone: '',
      email: ''
    },
    keysLocation: 'Na imobiliária',
    condominiumId: initialCondominiumId,
    userId: authUser?.id || currentUser?.id || '', // Usar autenticação real primeiro
    category: 'Médio',
    olx: {
      sendToOlx: false,
      olxType: 'STANDARD'
    },
    sendLeadsToQueue: false,
    createdAt: new Date()
  });

  // Função para formatar CEP
  const formatCep = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 5) {
      return numbers;
    }
    return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`;
  };

  // Função para buscar endereço por CEP
  const fetchAddressByCep = async () => {
    const cep = formData.location?.postalCode?.replace(/\D/g, '');
    
    if (!cep || cep.length !== 8) {
      if (formData.location?.postalCode && formData.location.postalCode.length > 0) {
        const cleanCep = formData.location.postalCode.replace(/\D/g, '');
        if (cleanCep.length > 0 && cleanCep.length < 8) {
          setCepError('CEP deve ter 8 dígitos');
        } else {
          setCepError('');
        }
      }
      return;
    }

    setCepLoading(true);
    setCepError('');

    try {
      const urls = [
        `https://viacep.com.br/ws/${cep}/json/`,
        `https://cdn.apicep.com/file/apicep/${cep}.json`,
        `https://brasilapi.com.br/api/cep/v1/${cep}`
      ];
      
      let data;
      
      for (const url of urls) {
        try {
          const response = await fetch(url);
          if (response.ok) {
            data = await response.json();
            if (!data.erro && !data.error && (data.cep || data.code)) {
              break;
            }
          }
        } catch (urlError) {
          continue;
        }
      }
      
      if (!data || data.erro || data.error) {
        setCepError('CEP não encontrado');
        return;
      }

      setFormData(prev => ({
        ...prev,
        location: {
          ...prev.location!,
          state: data.uf || data.state || prev.location?.state || 'SP',
          city: data.localidade || data.city || prev.location?.city || '',
          neighborhood: data.bairro || data.district || prev.location?.neighborhood || '',
          address: data.logradouro || data.address || prev.location?.address || '',
          streetNumber: prev.location?.streetNumber || '',
          complement: prev.location?.complement || '',
          postalCode: prev.location?.postalCode || '',
          country: 'Brasil',
          latitude: prev.location?.latitude || 0,
          longitude: prev.location?.longitude || 0
        }
      }));

    } catch (error) {
      setCepError('Erro ao buscar CEP. Tente novamente.');
    } finally {
      setCepLoading(false);
    }
  };

  const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCep(e.target.value);
    handleInputChange('location.postalCode', formatted);
    setCepError('');
    
    const cleanCep = formatted.replace(/\D/g, '');
    if (cleanCep.length === 8) {
      setTimeout(() => {
        fetchAddressByCep();
      }, 500);
    }
  };

  // Configuração dos sensores para drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Função para gerar ID único
  const generateId = () => Math.random().toString(36).substr(2, 9);

  // Função para validar arquivo de imagem
  const validateImageFile = (file: File): boolean => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      setUploadError('Apenas arquivos JPG, PNG e WEBP são permitidos');
      return false;
    }

    if (file.size > maxSize) {
      setUploadError('Arquivo deve ter no máximo 5MB');
      return false;
    }

    return true;
  };

  // Função para processar arquivos
  const processFiles = async (files: FileList) => {
    console.log('🚀 PropertyForm: Iniciando processFiles com', files.length, 'arquivos');
    setUploadError('');
    setIsProcessingImages(true);
    const newImages: any[] = [];
    const totalFiles = files.length;

    for (let i = 0; i < totalFiles; i++) {
      const file = files[i];
      console.log(`📁 PropertyForm: Processando arquivo ${i + 1}/${totalFiles}:`, file.name, 'Tipo:', file.type, 'Tamanho:', file.size);
      
      if (validateImageFile(file)) {
        console.log(`✅ PropertyForm: Arquivo ${i + 1} validado com sucesso`);
        
        try {
          console.log(`🎨 PropertyForm: Iniciando aplicação de marca d'água no arquivo ${i + 1}...`);
          
          // Processa o arquivo para formato suportado e aplica marca d'água
          const processedFile = await processImageFile(file);
          const watermarkedUrl = await uploadImageWithWatermark(processedFile);

          console.log(`✅ PropertyForm: Marca d'água aplicada com sucesso no arquivo ${i + 1}!`);
          console.log(`🔗 PropertyForm: URL da imagem ${i + 1} com marca d'água:`, watermarkedUrl);

          const id = generateId();
          
          newImages.push({
            id,
            url: watermarkedUrl,
            preview: watermarkedUrl,
            name: `${file.name}_watermarked.jpg`,
            primary: formData.media?.images?.length === 0 && newImages.length === 0,
            watermarked: true
          });
          
          console.log(`📋 PropertyForm: Imagem ${i + 1} adicionada ao array, total de novas imagens:`, newImages.length);
        } catch (error: any) {
          console.error(`❌ PropertyForm: Erro ao aplicar marca d'água no arquivo ${i + 1}:`, error);
          
          // Mostrar erro específico ao usuário
          const errorMessage = error.message || 'Erro desconhecido ao processar imagem';
          
          toast({
            title: 'Erro no Upload',
            description: `${file.name}: ${errorMessage}`,
            variant: 'destructive'
          });
          
          // Continue processing other files
          console.log(`⚠️ PropertyForm: Continuando com os outros arquivos...`);
        }
      } else {
        console.log(`❌ PropertyForm: Arquivo ${i + 1} não passou na validação`);
      }
    }

    console.log('📊 PropertyForm: Processamento concluído. Total de novas imagens processadas:', newImages.length);

    if (newImages.length > 0) {
      console.log('💾 PropertyForm: Adicionando imagens ao formData...');
      setFormData(prev => ({
        ...prev,
        media: {
          ...prev.media!,
          images: [...(prev.media?.images || []), ...newImages]
        }
      }));
      console.log('✅ PropertyForm: Imagens adicionadas ao formData com sucesso!');
      console.log('📈 PropertyForm: Total de imagens no formData:', (formData.media?.images?.length || 0) + newImages.length);
      
      toast({
        title: 'Upload Concluído',
        description: `${newImages.length} ${newImages.length === 1 ? 'imagem adicionada' : 'imagens adicionadas'} com sucesso!`,
      });
    } else {
      console.log('⚠️ PropertyForm: Nenhuma imagem foi processada');
    }
    
    setIsProcessingImages(false);
  };

  // Handlers para drag and drop
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await processFiles(e.dataTransfer.files);
    }
  };

  // Handler para input file
  const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      await processFiles(e.target.files);
    }
  };

  // Função para remover imagem
  const removeImage = (imageId: string) => {
    setFormData(prev => ({
      ...prev,
      media: {
        ...prev.media!,
        images: prev.media?.images?.filter(img => img.id !== imageId) || []
      }
    }));
  };

  // Função para manipular drag and drop das imagens
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setFormData(prev => {
        const images = prev.media?.images || [];
        const oldIndex = images.findIndex(img => img.id === active.id);
        const newIndex = images.findIndex(img => img.id === over?.id);

        const reorderedImages = arrayMove(images, oldIndex, newIndex);
        
        console.log('🔄 Reordenando imagens - de', oldIndex, 'para', newIndex);
        console.log('📸 Nova ordem das imagens:', reorderedImages.map(img => ({ id: img.id, name: img.name })));

        return {
          ...prev,
          media: {
            ...prev.media!,
            images: reorderedImages
          }
        };
      });
    }
  };

  // Função para definir imagem principal (agora move para primeira posição)
  const setPrimaryImage = (imageId: string) => {
    console.log('⭐ setPrimaryImage - Definindo imagem principal:', imageId);
    console.log('🖼️ setPrimaryImage - Imagens antes da alteração:', formData.media?.images?.map(img => ({ id: img.id, primary: img.primary })));
    
    setFormData(prev => {
      const images = prev.media?.images || [];
      
      // Encontrar a imagem que será marcada como principal
      const targetImageIndex = images.findIndex(img => img.id === imageId);
      if (targetImageIndex === -1) return prev;
      
      // Remover a imagem da posição atual e colocá-la no início
      const targetImage = images[targetImageIndex];
      const otherImages = images.filter(img => img.id !== imageId);
      
      // Criar nova lista com a imagem principal primeiro
      const reorderedImages = [
        { ...targetImage, primary: true },
        ...otherImages.map(img => ({ ...img, primary: false }))
      ];
      
      console.log('✅ setPrimaryImage - Imagem movida para primeira posição e marcada como principal');
      console.log('🖼️ setPrimaryImage - Nova ordem:', reorderedImages.map(img => ({ id: img.id, primary: img.primary, name: img.name })));
      
      return {
        ...prev,
        media: {
          ...prev.media!,
          images: reorderedImages
        }
      };
    });
  };

  // Buscar corretores quando for gerente ou diretor
  useEffect(() => {
    const fetchCorretores = async () => {
      console.log('🔍 Verificando se deve buscar corretores. isManagerOrDirector():', isManagerOrDirector());
      
      if (isManagerOrDirector()) {
        setLoadingCorretores(true);
        console.log('📋 Iniciando busca de corretores...');
        
        try {
          console.log('🌐 Fazendo requisição para Supabase...');
          console.log('🔑 Auth user:', authUser?.id);
          
          const { data, error } = await supabase
            .from('profiles')
            .select('user_id, first_name, last_name, email, role')
            .in('role', ['corretor', 'gerente', 'diretor'])
            .eq('is_active', true)
            .order('first_name');
          
          console.log('📊 Resultado da consulta:', { data, error });
          
          if (!error && data) {
            const corretoresList = data
              .filter(profile => profile.user_id) // Filtrar apenas perfis com user_id válido
              .map(profile => ({
                id: profile.user_id!,
                name: `${profile.first_name} ${profile.last_name}`
              }));
            
            console.log('✅ Corretores processados:', corretoresList);
            setCorretores(corretoresList);
          } else {
            console.error('❌ Erro ao buscar corretores:', error);
            
            // Em caso de erro, mostrar lista vazia mas permitir funcionamento
            setCorretores([]);
          }
        } catch (err) {
          console.error('💥 Erro na busca de corretores:', err);
          
          // Em caso de erro de rede, tentar carregamento alternativo
          setCorretores([]);
        } finally {
          setLoadingCorretores(false);
          console.log('🏁 Finalizou carregamento de corretores');
        }
      } else {
        console.log('🚫 Usuário não é gerente/diretor, não carregando corretores');
        setLoadingCorretores(false);
      }
    };

    // Buscar corretores apenas uma vez quando o componente monta
    if (corretores.length === 0 && !loadingCorretores) {
      console.log('🚀 Executando fetchCorretores pela primeira vez...');
      fetchCorretores();
    }
  }, [isManagerOrDirector]); // Adicionar isManagerOrDirector como dependência

  // Função para gerar descrição com IA
  const generateDescriptionWithAI = async () => {
    setGeneratingDescription(true);
    try {
      // Buscar dados do condomínio se existir
      const condominiumData = formData.condominiumId ? getCondominiumById(formData.condominiumId) : null;

      // Preparar dados do imóvel para enviar para a IA
      const propertyData = {
        title: formData.title,
        propertyType: formData.propertyType,
        transactionType: formData.transactionType,
        listPrice: formData.listPrice,
        rentalPrice: formData.rentalPrice,
        bedrooms: formData.bedrooms,
        suites: formData.suites,
        bathrooms: formData.bathrooms,
        garageQuantity: formData.garage?.quantity,
        areaBuilt: formData.livingArea,
        areaTotal: formData.livingArea,
        address: formData.location?.address,
        neighborhood: formData.location?.neighborhood,
        city: formData.location?.city,
        state: formData.location?.state,
        amenities: effectiveFeatures // Usar características efetivas (incluindo do condomínio)
      };

      const { data, error } = await supabase.functions.invoke('generate-property-description', {
        body: { propertyData, condominiumData }
      });

      if (error) {
        throw error;
      }

      if (data?.description) {
        handleInputChange('description', data.description);
      } else {
        throw new Error('Nenhuma descrição foi gerada');
      }
    } catch (error) {
      console.error('Erro ao gerar descrição:', error);
      alert('Erro ao gerar descrição com IA. Tente novamente mais tarde.');
    } finally {
      setGeneratingDescription(false);
    }
  };

  // Cleanup URLs quando componente desmonta
  useEffect(() => {
    return () => {
      formData.media?.images?.forEach(img => {
        if (img.preview) {
          URL.revokeObjectURL(img.preview);
        }
      });
    };
  }, []);

  useEffect(() => {
    if (property) {
      console.log('🔄 PropertyForm - Carregando propriedade para edição:', property);
      console.log('🏢 PropertyForm - condominiumId da propriedade:', property.condominiumId);
      console.log('✨ PropertyForm - features da propriedade:', property.features);
      console.log('🖼️ PropertyForm - Imagens da propriedade:', property.media?.images);
      
      // Verificar se há múltiplas imagens marcadas como principal
      const primaryImages = property.media?.images?.filter(img => img.primary === true) || [];
      console.log('⭐ PropertyForm - Imagens marcadas como principal:', primaryImages.length);
      
      if (primaryImages.length > 1) {
        console.warn('⚠️ PROBLEMA: Múltiplas imagens marcadas como principal!', primaryImages);
        
        // Corrigir: manter apenas a primeira como principal
        const correctedImages = property.media?.images?.map((img, index) => ({
          ...img,
          primary: index === 0 // Apenas a primeira será principal
        })) || [];
        
        console.log('🔧 PropertyForm - Corrigindo imagens, primeira será principal');
        
        const newData = {
          ...property,
          condominiumId: property.condominiumId || '', // Garantir que condominiumId seja preservado
          features: property.features || [], // Garantir que features sejam preservadas
          media: {
            ...property.media,
            images: correctedImages
          }
        };
        console.log('✨ PropertyForm - setFormData (com correção imagem), features set:', newData.features);
        console.log('✨ PropertyForm - setFormData (com correção imagem), features length:', newData.features?.length);
        setFormData(newData);
      } else {
        const newData = {
          ...property,
          condominiumId: property.condominiumId || '', // Garantir que condominiumId seja preservado
          features: property.features || [] // Garantir que features sejam preservadas
        };
        console.log('✨ PropertyForm - setFormData (sem correção imagem), features set:', newData.features);
        console.log('✨ PropertyForm - setFormData (sem correção imagem), features length:', newData.features?.length);
        setFormData(newData);
      }
      
      // Inicializar valores formatados
      setDisplayValues({
        listPrice: property.listPrice ? new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2
        }).format(property.listPrice) : '',
        rentalPrice: property.rentalPrice ? new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2
        }).format(property.rentalPrice) : '',
        propertyAdministrationFee: property.propertyAdministrationFee ? new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2
        }).format(property.propertyAdministrationFee) : '',
        yearlyTax: property.yearlyTax ? new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2
        }).format(property.yearlyTax) : ''
      });
      
      // Log das imagens após carregamento
      console.log('📸 PropertyForm - Imagens carregadas no formData:', property.media?.images);
      console.log('🏢 PropertyForm - condominiumId final no formData:', property.condominiumId);
      console.log('✨ PropertyForm - features finais no formData:', property.features);
      
      // Se o imóvel tem condomínio, carregar as imagens do condomínio
      if (property.condominiumId) {
        const condominium = getCondominiumById(property.condominiumId);
        if (condominium) {
          console.log('🏢 Carregando imagens do condomínio para edição:', condominium);
          
          const condImages = condominium.images?.map(img => ({
            id: `cond_${img.id}`,
            url: img.url,
            name: `${condominium.name} - ${img.name}`,
            primary: false,
            isCondominiumImage: true
          })) || [];
          
          console.log('📸 Imagens do condomínio carregadas (edição):', condImages);
          setCondominiumImages(condImages);
        }
      } else {
        setCondominiumImages([]);
      }
    }
  }, [property, getCondominiumById]);

  // Carregar dados do condomínio se selecionado
  useEffect(() => {
    if (initialCondominiumId) {
      const condominium = getCondominiumById(initialCondominiumId);
      if (condominium) {
        console.log('🏢 Carregando dados do condomínio:', condominium);
        
        // Carregar imagens do condomínio
        const condImages = condominium.images?.map(img => ({
          id: `cond_${img.id}`,
          url: img.url,
          name: `${condominium.name} - ${img.name}`,
          primary: false,
          isCondominiumImage: true
        })) || [];
        
        console.log('📸 Imagens do condomínio carregadas:', condImages);
        setCondominiumImages(condImages);
        
        setFormData(prev => ({
          ...prev,
          condominiumId: initialCondominiumId,
          // Keep existing property features - don't overwrite with condominium features
          location: {
            country: 'Brasil',
            state: condominium.address.state,
            city: condominium.address.city,
            neighborhood: condominium.address.neighborhood,
            address: condominium.address.street,
            streetNumber: condominium.address.number, // Número do condomínio
            complement: '',
            postalCode: condominium.address.postalCode,
            latitude: 0,
            longitude: 0
          }
        }));
      }
    } else {
      setCondominiumImages([]);
    }
  }, [initialCondominiumId, getCondominiumById]);

  // Atualizar displayOwnerPhone quando formData.owner.phone mudar
  useEffect(() => {
    if (formData.owner?.phone) {
      setDisplayOwnerPhone(formatPhone(formData.owner.phone));
    }
  }, [formData.owner?.phone]);

  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const keys = field.split('.');
      setFormData(prev => {
        const newData = { ...prev };
        let current: any = newData;
        
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) {
            current[keys[i]] = {};
          }
          current = current[keys[i]];
        }
        
        current[keys[keys.length - 1]] = value;
        return newData;
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Se mudou o condomínio, atualizar imagens
    if (field === 'condominiumId') {
      handleCondominiumChange(value);
    }
  };

  const handleCondominiumChange = (condominiumId: string) => {
    if (condominiumId) {
      const condominium = getCondominiumById(condominiumId);
      if (condominium) {
        console.log('🏢 Condomínio selecionado:', condominium);
        
        // Carregar imagens do condomínio
        const condImages = condominium.images?.map(img => ({
          id: `cond_${img.id}`,
          url: img.url,
          name: `${condominium.name} - ${img.name}`,
          primary: false,
          isCondominiumImage: true
        })) || [];
        
        console.log('📸 Imagens do condomínio carregadas:', condImages);
        setCondominiumImages(condImages);
        
        // Atualizar dados do condomínio
        setFormData(prev => ({
          ...prev,
          features: [...(condominium.features || [])],
          location: {
            country: 'Brasil',
            state: condominium.address.state,
            city: condominium.address.city,
            neighborhood: condominium.address.neighborhood,
            address: condominium.address.street,
            streetNumber: condominium.address.number,
            complement: '',
            postalCode: condominium.address.postalCode,
            latitude: 0,
            longitude: 0
          }
        }));
      }
    } else {
      setCondominiumImages([]);
    }
  };

  const handleOwnerPhoneChange = (value: string) => {
    const cleanPhone = parsePhone(value);
    setDisplayOwnerPhone(formatPhone(value));
    handleInputChange('owner.phone', cleanPhone);
  };

  // Função para combinar imagens do imóvel com do condomínio
  const getAllImages = () => {
    const propertyImages = formData.media?.images || [];
    return [...propertyImages, ...condominiumImages];
  };

  const toggleFeature = (featureValue: string) => {
    console.log('🎯 toggleFeature chamado:', featureValue);
    console.log('🎯 features atuais antes:', formData.features);
    setFormData(prev => {
      const newFeatures = prev.features?.includes(featureValue)
        ? prev.features.filter(f => f !== featureValue)
        : [...(prev.features || []), featureValue];
      console.log('🎯 features novas depois:', newFeatures);
      return {
        ...prev,
        features: newFeatures
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🔧 handleSubmit INICIADO');
    console.log('📊 authUser (Supabase):', authUser);
    console.log('📊 currentUser (Context):', currentUser);
    console.log('📋 formData completo:', formData);
    console.log('🎯 Captador selecionado - userId:', formData.userId);
    console.log('💰 Valores financeiros:', {
      propertyAdministrationFee: formData.propertyAdministrationFee,
      yearlyTax: formData.yearlyTax,
      listPrice: formData.listPrice,
      rentalPrice: formData.rentalPrice
    });
    
    // Usar o corretor selecionado (formData.userId) ou o usuário autenticado como fallback
    const activeUserId = formData.userId || authUser?.id || currentUser?.id;
    
    // Validar se há um usuário válido
    if (!activeUserId) {
      console.error('❌ Nenhum usuário autenticado encontrado');
      console.error('  authUser.id:', authUser?.id);
      console.error('  currentUser.id:', currentUser?.id);
      alert('Erro: Usuário não autenticado. Faça login novamente.');
      return;
    }
    
    // Validar campos obrigatórios básicos
    const camposObrigatoriosBasicos: Record<string, any> = {
      title: formData.title,
      propertyType: formData.propertyType,
      transactionType: formData.transactionType,
      bedrooms: formData.bedrooms,
      bathrooms: formData.bathrooms,
      livingArea: formData.livingArea,
      'location.neighborhood': formData.location?.neighborhood,
      'location.city': formData.location?.city,
      'location.state': formData.location?.state,
      'location.address': formData.location?.address,
      'owner.name': formData.owner?.name,
      'owner.phone': formData.owner?.phone
    };
    
    // Adicionar validações condicionais de preço baseadas no tipo de transação
    const camposObrigatorios: Record<string, any> = { ...camposObrigatoriosBasicos };
    
    if (formData.transactionType === 'Venda' || formData.transactionType === 'Venda/Aluguel') {
      camposObrigatorios['listPrice'] = formData.listPrice;
    }
    
    if (formData.transactionType === 'Aluguel' || formData.transactionType === 'Venda/Aluguel') {
      camposObrigatorios['rentalPrice'] = formData.rentalPrice;
    }
    
    console.log('🔍 Verificando campos obrigatórios:', camposObrigatorios);
    
    for (const [campo, valor] of Object.entries(camposObrigatorios)) {
      if (!valor || valor === '' || (typeof valor === 'number' && valor <= 0)) {
        console.error(`❌ Campo obrigatório vazio: ${campo} = ${valor}`);
        alert(`Erro: O campo ${campo} é obrigatório e está vazio.`);
        return;
      }
    }
    
    console.log('✅ Todos os campos obrigatórios preenchidos');
    
    try {
      const propertyData: Property = {
        ...formData,
        userId: activeUserId,
        listingId: formData.listingId || generatePropertyCode(formData.propertyType || ''),
        propertyCode: formData.propertyCode || generatePropertyCode(formData.propertyType || ''),
        detailViewUrl: `https://foxvendas.com.br/imovel/${formData.listingId || generatePropertyCode(formData.propertyType || '')}`,
        lastUpdateDate: new Date().toISOString(),
        // Usar características efetivas (selecionadas + do condomínio)
        features: effectiveFeatures,
        // Salvar apenas imagens do próprio imóvel (não do condomínio)
        media: {
          ...formData.media,
          images: formData.media?.images || []
        }
      } as Property;

      console.log('🎯 PropertyForm - Características sendo enviadas:', effectiveFeatures);

      console.log('🏗️ propertyData criado:', propertyData);
      console.log('💰 Valores financeiros no propertyData:', {
        propertyAdministrationFee: propertyData.propertyAdministrationFee,
        yearlyTax: propertyData.yearlyTax,
        listPrice: propertyData.listPrice,
        rentalPrice: propertyData.rentalPrice
      });
      console.log('🔍 Tipo de transação:', propertyData.transactionType);
      console.log('🎯 Valor que deveria aparecer no card:', 
        propertyData.transactionType === 'Aluguel' ? propertyData.rentalPrice : 
        propertyData.transactionType === 'Venda' ? propertyData.listPrice : 
        'Ambos os valores'
      );
      console.log('📞 Chamando onSave...');
      
      await onSave(propertyData);
      
      console.log('✅ onSave chamado com sucesso');
    } catch (error: any) {
      console.error('💥 Erro no handleSubmit:', error);
      alert(`Erro ao ${property ? 'atualizar' : 'salvar'} imóvel: ${error.message || error}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Características organizadas por tipo de imóvel
  const featuresByPropertyType: { [key: string]: string[] } = {
    // Características para Apartamentos/Flats
    'apartment': [
      'Adaptado para deficientes',
      'Aquecimento central',
      'Ar condicionado',
      'Área de serviço',
      'Armários embutidos',
      'Casa inteligente',
      'Closet',
      'Cozinha planejada',
      'Dependência de empregada',
      'Despensa',
      'Energia solar',
      'Hidromassagem',
      'Lareira',
      'Lavabo',
      'Mobiliado',
      'Pet friendly',
      'Sacada',
      'Semi-mobiliado',
      'Terraço',
      'Varanda',
      'Varanda gourmet',
      'Vista cidade',
      'Vista jardim',
      'Vista lago',
      'Vista mar',
      'Vista montanha',
      'Vista panorâmica'
    ],
    
    // Características para Casas/Sobrados
    'house': [
      'Adaptado para deficientes',
      'Aquecimento central',
      'Ar condicionado',
      'Área de serviço',
      'Armários embutidos',
      'Árvore frutífera',
      'Casa inteligente',
      'Cerca',
      'Closet',
      'Cozinha planejada',
      'Dependência de empregada',
      'Despensa',
      'Edícula',
      'Energia solar',
      'Fossa séptica',
      'Hidromassagem',
      'Horta',
      'Jardim privativo',
      'Lareira',
      'Lavabo',
      'Mobiliado',
      'Pet friendly',
      'Piscina privativa',
      'Poço',
      'Poço artesiano',
      'Pomar',
      'Portão eletrônico',
      'Quintal',
      'Semi-mobiliado',
      'Terraço',
      'Varanda',
      'Varanda gourmet'
    ],
    
    // Características para Terrenos/Lotes
    'land': [
      'Água encanada',
      'Árvo frutífera',
      'Cascalho',
      'Cerca',
      'Energia elétrica',
      'Fossa séptica',
      'Murado',
      'Portão',
      'Poço',
      'Poço artesiano',
      'Públicos essenciais',
      'Rua asfaltada'
    ],
    
    // Características para Salas Comerciais/Escritórios
    'office': [
      'Adaptado para deficientes',
      'Ar condicionado',
      'Armários embutidos',
      'Banheiro privativo',
      'Cabeamento estruturado',
      'Cozinha',
      'Copa',
      'Coworking',
      'Divisórias',
      'Elevador',
      'Energia solar',
      'Estacionamento',
      'Lavabo',
      'Mobiliado',
      'Piso elevado',
      'Recepção',
      'Sala de reunião',
      'Semi-mobiliado',
      'Sistema de alarme',
      'Vigilância 24h'
    ],
    
    // Características para Pontos Comerciais/Lojas
    'commercial': [
      'Adaptado para deficientes',
      'Ar condicionado',
      'Banheiro',
      'Copa',
      'Deposito',
      'Estacionamento',
      'Frente para rua',
      'Instalação de loja',
      'Mezanino',
      'Sistema de alarme',
      'Vigilância 24h',
      'Vitrine'
    ],
    
    // Características para Galpões/Armazéns
    'warehouse': [
      'Adaptado para deficientes',
      'Água encanada',
      'Banheiro',
      'Copa',
      'Depósito',
      'Docas',
      'Energia elétrica',
      'Escritório',
      'Estacionamento',
      'Pé direito alto',
      'Portão para caminhão',
      'Segurança 24h'
    ],
    
    // Características para Fazendas/Sítios/Chácaras
    'farm': [
      'Água encanada',
      'Área de plantio',
      'Árvore frutífera',
      'Canil',
      'Casa de caseiro',
      'Casa sede',
      'Celeiro',
      'Cerca',
      'Curral',
      'Energia elétrica',
      'Fossa séptica',
      'Horta',
      'Lago',
      'Pasto',
      'Piscina',
      'Poço',
      'Poço artesiano',
      'Pomar',
      'Porteira',
      'Quintal',
      'Represa',
      'Rio'
    ]
  };

  // Função para obter as características apropriadas baseado no tipo de imóvel
  const getRelevantFeatures = (): string[] => {
    const propertyType = formData.propertyType || '';
    
    console.log('🏠 PropertyForm - Tipo de imóvel:', propertyType);
    
    // Mapear tipos VRSync para categorias de características
    // Apartamentos, Flats, Coberturas, Lofts, Studios, Kitnet
    if (propertyType.includes('Apartment') || propertyType.includes('Flat') || 
        propertyType.includes('Penthouse') || propertyType.includes('Loft') ||
        propertyType.includes('Studio') || propertyType.includes('Duplex') ||
        propertyType.includes('Triplex') || propertyType.includes('Quadruplex')) {
      console.log('✅ Usando características de APARTAMENTO');
      return featuresByPropertyType['apartment'];
    }
    
    // Casas, Sobrados, Casa de Vila
    if (propertyType.includes('Home') || propertyType.includes('Sobrado') ||
        propertyType.includes('Village House') || propertyType.includes('Condominium')) {
      console.log('✅ Usando características de CASA');
      return featuresByPropertyType['house'];
    }
    
    // Terrenos e Lotes
    if (propertyType.includes('Land Lot')) {
      console.log('✅ Usando características de TERRENO');
      return featuresByPropertyType['land'];
    }
    
    // Salas Comerciais e Escritórios
    if (propertyType.includes('Office')) {
      console.log('✅ Usando características de SALA COMERCIAL');
      return featuresByPropertyType['office'];
    }
    
    // Lojas e Pontos Comerciais
    if (propertyType.includes('Shop') || propertyType.includes('Business Point')) {
      console.log('✅ Usando características de LOJA');
      return featuresByPropertyType['commercial'];
    }
    
    // Galpões e Armazéns
    if (propertyType.includes('Warehouse') || propertyType.includes('Industrial')) {
      console.log('✅ Usando características de GALPÃO');
      return featuresByPropertyType['warehouse'];
    }
    
    // Fazendas, Sítios e Chácaras
    if (propertyType.includes('Farm') || propertyType.includes('Ranch')) {
      console.log('✅ Usando características de FAZENDA');
      return featuresByPropertyType['farm'];
    }
    
    // Default: retornar características de apartamento
    console.log('⚠️ Tipo não reconhecido, usando características de APARTAMENTO como padrão');
    return featuresByPropertyType['apartment'];
  };

  // Função para obter características do condomínio selecionado
  const getCondominiumFeatures = () => {
    if (!formData.condominiumId) return [];
    const condominium = getCondominiumById(formData.condominiumId);
    return condominium?.features || [];
  };

  // Características efetivas (selecionadas + herdadas do condomínio)
  const effectiveFeatures = useMemo(() => {
    const condominiumFeatures = getCondominiumFeatures();
    const selectedFeatures = formData.features || [];
    const allFeatures = [...selectedFeatures, ...condominiumFeatures];
    return [...new Set(allFeatures)];
  }, [formData.features, formData.condominiumId]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">
            {property ? 'Editar Imóvel' : 'Novo Imóvel'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {/* Informações Básicas */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <Building className="w-5 h-5 mr-2" />
              Informações Básicas
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Título do Anúncio *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Ex: Apartamento Moderno no Centro"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Código do Imóvel (Gerado Automaticamente)
                </label>
                <input
                  type="text"
                  disabled
                  value={formData.listingId}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                  placeholder="Será gerado automaticamente"
                />
                <p className="text-sm text-gray-500 mt-1">
                  O código é gerado automaticamente e não pode ser alterado
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Imóvel *
                </label>
                <select
                  required
                  value={formData.propertyType || ''}
                  onChange={(e) => handleInputChange('propertyType', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="">Selecione o tipo de imóvel</option>
                  <optgroup label="Residencial">
                    <option value="Residential / Apartment">Apartamento</option>
                    <option value="Residential / Home">Casa</option>
                    <option value="Residential / Condominium">Casa de Condomínio</option>
                    <option value="Residential / Penthouse">Cobertura</option>
                    <option value="Residential / Flat">Flat</option>
                    <option value="Residential / Studio">Studio</option>
                    <option value="Residential / Loft">Loft</option>
                    <option value="Residential / Duplex">Duplex</option>
                    <option value="Residential / Triplex">Triplex</option>
                    <option value="Residential / Quadruplex">Quadruplex</option>
                    <option value="Residential / Village House">Casa de Vila</option>
                    <option value="Residential / Farm">Chácara</option>
                    <option value="Residential / Farm Ranch">Fazenda</option>
                    <option value="Residential / Sobrado">Sobrado</option>
                    <option value="Residential / Land Lot">Terreno</option>
                  </optgroup>
                  <optgroup label="Comercial">
                    <option value="Commercial / Building">Prédio</option>
                    <option value="Commercial / Office">Sala</option>
                    <option value="Commercial / Shop">Loja</option>
                    <option value="Commercial / Warehouse">Galpão</option>
                    <option value="Commercial / Industrial Shed">Barracão</option>
                    <option value="Commercial / Business Point">Ponto Comercial</option>
                    <option value="Commercial / Land Lot">Terreno Comercial</option>
                    <option value="Commercial / Industrial">Galpão Industrial</option>
                  </optgroup>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Transação *
                </label>
                <select
                  required
                  value={formData.transactionType}
                  onChange={(e) => handleInputChange('transactionType', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="Venda">Venda</option>
                  <option value="Aluguel">Aluguel</option>
                  <option value="Venda/Aluguel">Venda/Aluguel</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Publicação *
                </label>
                <select
                  required
                  value={formData.publicationType}
                  onChange={(e) => handleInputChange('publicationType', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="STANDARD">Padrão</option>
                  <option value="PREMIUM">Premium</option>
                </select>
              </div>
              
              {/* Seleção de Condomínio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Condomínio (Opcional)
                </label>
                <select
                  value={formData.condominiumId || ''}
                  onChange={(e) => handleInputChange('condominiumId', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="">Selecione um condomínio</option>
                  {condominiums
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(cond => (
                      <option key={cond.id} value={cond.id}>
                        {cond.name}
                      </option>
                    ))}
                </select>
                {formData.condominiumId && (
                  <p className="text-blue-600 text-sm mt-1">
                    📍 Dados e imagens do condomínio serão incluídos automaticamente
                  </p>
                )}
              </div>
            </div>

            {/* Campo de seleção de captador - apenas para gerentes e diretores */}
            {isManagerOrDirector() && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-md font-semibold text-blue-800 mb-3 flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Corretor Captador
                </h4>
                <p className="text-sm text-blue-700 mb-3">
                  Como gerente/diretor, você pode escolher outro corretor como captador deste imóvel.
                </p>
                
                {/* Checkbox para enviar leads direto para fila */}
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      id="sendLeadsToQueue"
                      checked={formData.sendLeadsToQueue || false}
                      onChange={(e) => handleInputChange('sendLeadsToQueue', e.target.checked)}
                      className="mt-1 h-4 w-4 text-amber-600 focus:ring-amber-500 border-amber-300 rounded"
                    />
                    <div className="flex-1">
                      <label htmlFor="sendLeadsToQueue" className="text-sm font-medium text-amber-800 cursor-pointer">
                        Enviar lead direto para fila
                      </label>
                      <p className="text-xs text-amber-700 mt-1">
                        Quando marcado, os leads deste imóvel vão ignorar o corretor responsável e seguir direto para a fila de distribuição
                      </p>
                    </div>
                  </div>
                </div>

                {/* Mostrar seleção de captador apenas se NÃO for para fila */}
                {!formData.sendLeadsToQueue && (
                  <div>
                    <label className="block text-sm font-medium text-blue-700 mb-2">
                      Selecionar Captador
                    </label>
                    {loadingCorretores ? (
                      <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                        <span className="ml-2 text-sm text-blue-600">Carregando corretores...</span>
                      </div>
                    ) : (
                      <select
                        value={formData.userId || currentUser?.id || ''}
                        onChange={(e) => handleInputChange('userId', e.target.value)}
                        className="w-full px-4 py-3 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                      >
                        <option value={currentUser?.id || ''}>
                          Eu mesmo (manter como captador)
                        </option>
                        {corretores.map(corretor => (
                          <option key={corretor.id} value={corretor.id}>
                            {corretor.name}
                          </option>
                        ))}
                      </select>
                    )}
                    <p className="text-sm text-blue-600 mt-1">
                      {formData.userId === currentUser?.id 
                        ? 'Você será o captador deste imóvel' 
                        : 'Outro corretor será o captador deste imóvel'
                      }
                    </p>
                  </div>
                )}

                {/* Mensagem quando checkbox está marcado */}
                {formData.sendLeadsToQueue && (
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>Fila de distribuição ativada:</strong> Os leads deste imóvel seguirão a ordem da fila automática, ignorando o captador específico.
                    </p>
                  </div>
                )}
              </div>
            )}

          </div>

          {/* Valores */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <DollarSign className="w-5 h-5 mr-2" />
              Valores
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Campo de Preço de Venda - aparece apenas para Venda ou Venda/Aluguel */}
              {(formData.transactionType === 'Venda' || formData.transactionType === 'Venda/Aluguel') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preço de Venda (R$) *
                  </label>
                  <input
                    type="text"
                    required
                    value={displayValues.listPrice}
                    onChange={(e) => handleCurrencyChange('listPrice', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="R$ 850.000,00"
                  />
                </div>
              )}

              {/* Campo de Preço de Aluguel - aparece apenas para Aluguel ou Venda/Aluguel */}
              {(() => {
                const shouldShow = formData.transactionType === 'Aluguel' || formData.transactionType === 'Venda/Aluguel' || 
                                 formData.transactionType === 'For Rent' || formData.transactionType === 'rent';
                console.log('🔍 Campo Aluguel - transactionType:', formData.transactionType, 'shouldShow:', shouldShow);
                return shouldShow;
              })() && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preço de Aluguel (R$) {(formData.transactionType === 'Aluguel' || formData.transactionType === 'For Rent' || formData.transactionType === 'rent') ? '*' : ''}
                  </label>
                  <input
                    type="text"
                    required={formData.transactionType === 'Aluguel' || formData.transactionType === 'For Rent' || formData.transactionType === 'rent'}
                    value={displayValues.rentalPrice}
                    onChange={(e) => handleCurrencyChange('rentalPrice', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="R$ 3.500,00"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Condomínio (R$)
                </label>
                <input
                  type="text"
                  value={displayValues.propertyAdministrationFee}
                  onChange={(e) => handleCurrencyChange('propertyAdministrationFee', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="R$ 450,00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  IPTU Mensal (R$)
                </label>
                <input
                  type="text"
                  value={displayValues.yearlyTax}
                  onChange={(e) => handleCurrencyChange('yearlyTax', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="R$ 3.500,00"
                />
              </div>
            </div>
          </div>

          {/* Características do Imóvel */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <Ruler className="w-5 h-5 mr-2" />
              Características do Imóvel
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Área Construída (m²) *
                </label>
                <input
                  type="number"
                  required
                  value={formData.livingArea || ''}
                  onChange={(e) => handleInputChange('livingArea', e.target.value === '' ? 0 : Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="95"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Área Total (m²)
                </label>
                <input
                  type="number"
                  value={formData.totalArea || ''}
                  onChange={(e) => handleInputChange('totalArea', e.target.value === '' ? undefined : Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="120"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dormitórios *
                </label>
                <input
                  type="number"
                  required
                  value={formData.bedrooms || ''}
                  onChange={(e) => handleInputChange('bedrooms', e.target.value === '' ? 0 : Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="3"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Suítes
                </label>
                <input
                  type="number"
                  value={formData.suites || ''}
                  onChange={(e) => handleInputChange('suites', e.target.value === '' ? 0 : Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Banheiros *
                </label>
                <input
                  type="number"
                  required
                  value={formData.bathrooms || ''}
                  onChange={(e) => handleInputChange('bathrooms', e.target.value === '' ? 0 : Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="3"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vagas Garagem *
                </label>
                <input
                  type="number"
                  required
                  value={formData.garage?.quantity || ''}
                  onChange={(e) => handleInputChange('garage.quantity', e.target.value === '' ? 0 : Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo Garagem
                </label>
                <select
                  value={formData.garage?.type}
                  onChange={(e) => handleInputChange('garage.type', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="Covered">Coberta</option>
                  <option value="Parking Space">Descoberta</option>
                </select>
              </div>
            </div>

            {/* Características Adicionais */}
            <div className="mt-6">
              <h4 className="text-md font-semibold text-gray-800 mb-3">
                Características Adicionais
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                Selecione todas as características que o imóvel possui:
              </p>
              
            {/* Seção de características herdadas do condomínio */}
            {formData.condominiumId && getCondominiumFeatures().length > 0 && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h5 className="text-sm font-semibold text-blue-800 mb-2">
                  📋 Características herdadas do condomínio ({getCondominiumFeatures().length})
                </h5>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1">
                  {getCondominiumFeatures().map((feature) => (
                    <span key={feature} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                      {translateAmenity(feature)}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  ℹ️ Estas características são automaticamente incluídas por fazer parte do condomínio
                </p>
              </div>
            )}
            
            {/* Seção de características específicas do imóvel */}
            <div className="space-y-2">
              <h5 className="text-sm font-semibold text-gray-800">
                🏠 Características específicas {formData.propertyType ? `para ${formData.propertyType}` : 'da unidade'}
              </h5>
              
              {!formData.propertyType && (
                <p className="text-sm text-amber-600 mb-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-200">
                  ⚠️ Selecione o tipo de imóvel acima para ver características específicas
                </p>
              )}
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-4">
                {getRelevantFeatures().map((feature: string) => {
                  const isSelected = formData.features?.includes(feature);
                  return (
                    <label
                      key={feature}
                      className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                    >
                      <button
                        type="button"
                        onClick={() => toggleFeature(feature)}
                        className="flex-shrink-0"
                      >
                        {isSelected ? (
                          <CheckSquare className="w-4 h-4 text-orange-500" />
                        ) : (
                          <Square className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                      <span className="text-sm text-gray-700">
                        {feature}
                      </span>
                    </label>
                  );
                })}
              </div>

              <div className="text-sm text-gray-500 mt-2">
                {formData.features?.length || 0} características específicas da unidade selecionadas
                {formData.condominiumId && (
                  <span className="text-blue-600 ml-2">
                    + {getCondominiumFeatures().length} características do condomínio
                    = {effectiveFeatures.length} total
                  </span>
                )}
              </div>
            </div>
            </div>
          </div>

          {/* Localização */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <MapPin className="w-5 h-5 mr-2" />
              Localização
              {formData.condominiumId && (
                <span className="ml-2 text-sm text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                  Dados do condomínio carregados
                </span>
              )}
            </h3>
            
            <div className="space-y-4">
              {/* CEP primeiro */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CEP *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      required
                      value={formData.location?.postalCode}
                      onChange={handleCepChange}
                      onBlur={fetchAddressByCep}
                      disabled={!!formData.condominiumId}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="01234-567"
                      maxLength={9}
                    />
                    {cepLoading && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-500"></div>
                      </div>
                    )}
                  </div>
                  {cepError && (
                    <p className="text-red-500 text-sm mt-1">{cepError}</p>
                  )}
                  {formData.condominiumId && (
                    <p className="text-blue-600 text-sm mt-1">
                      CEP carregado do condomínio
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estado *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.location?.state}
                    onChange={(e) => handleInputChange('location.state', e.target.value)}
                    disabled={!!formData.condominiumId}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="SP"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cidade *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.location?.city}
                    onChange={(e) => handleInputChange('location.city', e.target.value)}
                    disabled={!!formData.condominiumId}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="São Paulo"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bairro *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.location?.neighborhood}
                    onChange={(e) => handleInputChange('location.neighborhood', e.target.value)}
                    disabled={!!formData.condominiumId}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Centro"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Endereço *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.location?.address}
                    onChange={(e) => handleInputChange('location.address', e.target.value)}
                    disabled={!!formData.condominiumId}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Rua das Flores"
                  />
                  {formData.condominiumId && (
                    <p className="text-blue-600 text-sm mt-1">
                      Endereço do condomínio
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Número {formData.condominiumId ? 'do Condomínio' : ''} {formData.condominiumId ? '*' : ''}
                  </label>
                  <input
                    type="text"
                    required={!!formData.condominiumId}
                    value={formData.location?.streetNumber}
                    onChange={(e) => handleInputChange('location.streetNumber', e.target.value)}
                    disabled={!!formData.condominiumId}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="150"
                  />
                  {formData.condominiumId && (
                    <p className="text-blue-600 text-sm mt-1">
                      Número carregado do condomínio
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Torre
                  </label>
                  <input
                    type="text"
                    value={formData.tower}
                    onChange={(e) => handleInputChange('tower', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Torre A"
                  />
                  <p className="text-gray-500 text-sm mt-1">
                    Torre do empreendimento (não será divulgada publicamente)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Complemento
                  </label>
                  <input
                    type="text"
                    value={formData.location?.complement}
                    onChange={(e) => handleInputChange('location.complement', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Apto 45"
                  />
                  <p className="text-gray-500 text-sm mt-1">
                    Identificação específica do apartamento/casa (visível apenas internamente)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Descrição do Imóvel */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              Descrição do Imóvel
            </h3>
            <p className="text-sm text-gray-600">
              A descrição será usada no anúncio e pode ser gerada automaticamente com base nas características do imóvel.
            </p>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  Descrição *
                </label>
                <button
                  type="button"
                  disabled={generatingDescription}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors text-sm flex items-center space-x-2"
                  onClick={generateDescriptionWithAI}
                >
                  {generatingDescription ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Gerando...</span>
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      <span>Gerar com Inteligência Artificial</span>
                    </>
                  )}
                </button>
              </div>
              
              <textarea
                required
                rows={6}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Descreva manualmente as principais características do imóvel, localização, diferenciais e pontos de interesse próximos... Ou use o botão acima para gerar automaticamente com Inteligência Artificial."
              />
              
              <div className="text-sm text-gray-500">
                {formData.description?.length || 0} caracteres
              </div>
            </div>
          </div>

          {/* Proprietário */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <User className="w-5 h-5 mr-2" />
              Dados do Proprietário
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome *
                </label>
                <input
                  type="text"
                  required
                  value={formData.owner?.name}
                  onChange={(e) => handleInputChange('owner.name', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Nome do proprietário"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefone *
                </label>
                <input
                  type="tel"
                  required
                  value={displayOwnerPhone}
                  onChange={(e) => handleOwnerPhoneChange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="(11) 99999-9999"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-mail *
                </label>
                <input
                  type="email"
                  required
                  value={formData.owner?.email}
                  onChange={(e) => handleInputChange('owner.email', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="proprietario@email.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Localização das Chaves *
              </label>
              <select
                required
                value={formData.keysLocation}
                onChange={(e) => handleInputChange('keysLocation', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="Na imobiliária">Na imobiliária</option>
                <option value="Com o proprietário">Com o proprietário</option>
              </select>
            </div>
          </div>

          {/* Upload de Imagens */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <ImageIcon className="w-5 h-5 mr-2" />
              Imagens do Imóvel
            </h3>
            
            {/* Área de Upload */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive 
                  ? 'border-orange-500 bg-orange-50' 
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center space-y-4">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <Camera className="w-8 h-8 text-gray-400" />
                </div>
                
                <div>
                  <p className="text-lg font-medium text-gray-700 mb-2">
                    Arraste as imagens aqui
                  </p>
                  <p className="text-sm text-gray-500 mb-4">
                    ou clique no botão abaixo para selecionar
                  </p>
                  
                  <label className={`px-6 py-3 rounded-lg transition-colors cursor-pointer inline-flex items-center space-x-2 ${
                    isProcessingImages 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-orange-500 hover:bg-orange-600'
                  } text-white`}>
                    <Upload className="w-5 h-5" />
                    <span>{isProcessingImages ? 'Processando...' : 'Selecionar Imagens'}</span>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileInput}
                      className="hidden"
                      disabled={isProcessingImages}
                    />
                  </label>
                </div>
                
                <p className="text-xs text-gray-400">
                  Formatos aceitos: JPG, PNG, WEBP • Máximo: 5MB por imagem
                </p>
              </div>
            </div>

            {/* Animação de processamento */}
            {isProcessingImages && (
              <div className="animate-fade-in bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-center justify-center space-x-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500"></div>
                  <div className="text-orange-700">
                    <p className="font-medium">Processando fotos...</p>
                    <p className="text-sm">Aplicando marca d'água e otimizando imagens</p>
                  </div>
                </div>
              </div>
            )}

            {/* Erro de Upload */}
            {uploadError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-700 text-sm">{uploadError}</p>
              </div>
            )}

            {/* Preview das Imagens com Drag and Drop */}
            {(formData.media?.images && formData.media.images.length > 0) || condominiumImages.length > 0 ? (
              <div className="space-y-3">
                <h4 className="text-md font-medium text-gray-700">
                  Imagens Selecionadas ({getAllImages().length})
                  <span className="text-sm text-gray-500 ml-2">
                    Arraste para reordenar • Clique na estrela para definir como capa
                  </span>
                </h4>
                
                {/* Informação sobre imagens do condomínio */}
                {condominiumImages.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-blue-800 text-sm font-medium">
                      📸 {condominiumImages.length} imagem(ns) do condomínio incluída(s) automaticamente
                    </p>
                    <p className="text-blue-700 text-xs mt-1">
                      As imagens do imóvel aparecerão primeiro, seguidas pelas do condomínio no anúncio
                    </p>
                  </div>
                )}
                
                <DndContext 
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext 
                    items={formData.media?.images?.map(img => img.id).filter(Boolean) as string[] || []}
                    strategy={rectSortingStrategy}
                  >
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {/* Imagens do imóvel (editáveis) */}
                      {formData.media?.images?.map((image) => (
                        <SortableImageItem
                          key={image.id}
                          image={image}
                          onSetPrimary={setPrimaryImage}
                          onRemove={removeImage}
                        />
                      ))}
                      
                      {/* Imagens do condomínio (apenas visualização) */}
                      {condominiumImages.map((image) => (
                        <div key={image.id} className="relative group">
                          <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 border-2 border-blue-200">
                            <img
                              src={image.url}
                              alt={image.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          
                          {/* Badge de imagem do condomínio */}
                          <div className="absolute top-2 left-2 bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                            Condomínio
                          </div>
                          
                          {/* Nome do arquivo */}
                          <p className="text-xs text-gray-500 mt-1 truncate" title={image.name}>
                            {image.name}
                          </p>
                        </div>
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            ) : null}

            {/* URL do YouTube */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vídeo do YouTube (opcional)
              </label>
              <input
                type="url"
                value={formData.media?.youtubeVideoUrl}
                onChange={(e) => handleInputChange('media.youtubeVideoUrl', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="https://www.youtube.com/watch?v=..."
              />
            </div>

            {/* URL do Tour Virtual */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tour Virtual 360° (opcional)
              </label>
              <input
                type="url"
                value={formData.media?.virtualTourUrl}
                onChange={(e) => handleInputChange('media.virtualTourUrl', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="https://matterport.com/..."
              />
            </div>
          </div>

          {/* Categoria do Imóvel */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">
              Categoria do Imóvel
            </h3>
            <p className="text-sm text-gray-600">
              Esta categoria será usada apenas para organização no seu site
            </p>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categoria *
              </label>
              <select
                required
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="Luxo">Luxo</option>
                <option value="Alto Padrão">Alto Padrão</option>
                <option value="Médio/Alto">Médio/Alto</option>
                <option value="Médio">Médio</option>
                <option value="Econômico">Econômico</option>
              </select>
            </div>
          </div>

          {/* Configurações para OLX */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <Building className="w-5 h-5 mr-2" />
              Portais Imobiliários (Grupo OLX)
            </h3>
            <p className="text-sm text-gray-600">
              Configure se este imóvel será enviado para os portais do Grupo OLX (OLX, Viva Real, Zap Imóveis) via XML
            </p>
            
            <div className="space-y-4">
              <label className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.olx?.sendToOlx}
                  onChange={(e) => handleInputChange('olx.sendToOlx', e.target.checked)}
                  className="w-5 h-5 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
                />
                <div className="flex items-center space-x-2">
                  <Building className="w-5 h-5 text-blue-500" />
                  <div>
                    <span className="font-medium text-gray-800">Enviar para Portais do Grupo OLX</span>
                    <p className="text-sm text-gray-600">Este imóvel será incluído no XML enviado para OLX, Viva Real e Zap Imóveis</p>
                  </div>
                </div>
              </label>

              {formData.olx?.sendToOlx && (
                <div className="ml-8 space-y-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Anúncio nos Portais *
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="radio"
                        name="olxType"
                        value="STANDARD"
                        checked={formData.olx?.olxType === 'STANDARD'}
                        onChange={(e) => handleInputChange('olx.olxType', e.target.value)}
                        className="w-4 h-4 text-green-500 border-gray-300 focus:ring-green-500"
                      />
                      <div>
                        <span className="font-medium text-gray-800">Anúncio Padrão</span>
                        <p className="text-sm text-gray-600">Anúncio normal nos portais</p>
                      </div>
                    </label>
                    
                    <label className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="radio"
                        name="olxType"
                        value="PREMIUM"
                        checked={formData.olx?.olxType === 'PREMIUM'}
                        onChange={(e) => handleInputChange('olx.olxType', e.target.value)}
                        className="w-4 h-4 text-orange-500 border-gray-300 focus:ring-orange-500"
                      />
                      <div>
                        <span className="font-medium text-gray-800">Anúncio Destaque</span>
                        <p className="text-sm text-gray-600">Anúncio em destaque nos portais (sujeito a limite da conta)</p>
                      </div>
                    </label>
                  </div>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-yellow-800 text-sm">
                      <strong>Importante:</strong> Se o limite de destaques da conta for atingido, 
                      os próprios portais converterão automaticamente os anúncios excedentes para padrão.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Botões de Ação */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving || isProcessingImages}
              onClick={() => {
                console.log('🔘 Botão SALVAR clicado!');
                console.log('🔄 isSaving:', isSaving);
                console.log('🖼️ isProcessingImages:', isProcessingImages);
                console.log('❌ disabled:', isSaving || isProcessingImages);
              }}
              className={`px-6 py-3 rounded-lg transition-all duration-200 flex items-center space-x-2 ${
                isSaving || isProcessingImages
                  ? 'bg-gray-400 cursor-not-allowed transform scale-95'
                  : 'bg-orange-500 hover:bg-orange-600 hover:scale-105'
              } text-white`}
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Salvando...</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>{property ? 'Atualizar' : 'Salvar'} Imóvel</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PropertyForm;